import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  ClearWorkflowReason,
  SetWorkflowReason,
  WorkflowState,
} from '../types';

const initialState: WorkflowState = {
  activeWorkflowEncounterId: null,
  lastAccessedAt: null,
};

/**
 * Encounter Workflow Slice
 *
 * Manages the "active workflow" encounter state:
 * - Which encounter is currently being worked on (recording/editing)
 * - When it was last accessed (for staleness detection)
 *
 * This is CLIENT STATE ONLY - does not represent server state
 */
export const encounterWorkflowSlice = createSlice({
  name: 'encounters/workflow',
  initialState,
  reducers: {
    /**
     * Set the active workflow encounter
     *
     * Called when:
     * - Creating new encounter
     * - Starting recording
     * - User explicitly resumes draft
     */
    setActiveWorkflowEncounter: (
      state,
      action: PayloadAction<{
        encounterId: string;
        reason: SetWorkflowReason;
      }>
    ) => {
      state.activeWorkflowEncounterId = action.payload.encounterId;
      state.lastAccessedAt = Date.now();
    },

    /**
     * Clear the active workflow encounter
     *
     * Called when:
     * - Encounter completed
     * - Encounter deleted
     * - User explicitly starts new encounter
     * - Staleness check fails
     */
    clearActiveWorkflowEncounter: (
      state,
      action: PayloadAction<{
        reason: ClearWorkflowReason;
      }>
    ) => {
      state.activeWorkflowEncounterId = null;
      state.lastAccessedAt = null;
    },

    /**
     * Touch the workflow timestamp
     *
     * Called periodically (every 5 min) while user is actively working
     * Extends the staleness window for long editing sessions
     */
    touchWorkflow: state => {
      if (state.activeWorkflowEncounterId) {
        state.lastAccessedAt = Date.now();
      }
    },
  },
});

export const {
  setActiveWorkflowEncounter,
  clearActiveWorkflowEncounter,
  touchWorkflow,
} = encounterWorkflowSlice.actions;

export default encounterWorkflowSlice.reducer;
